import { Button } from 'blaze-ui/Button'
import Styles from './PerformanceInsights.module.scss'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { FooterButtons } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { InitiateOauthTrigger } from 'components/ConnectIntegrationTrigger/InitiateOauthTrigger'
import { useConnectAccountPopup } from 'components/Onboarding/useConnectAccountPopup'
import { organicChannels } from '../InsightChannelsSelection/utils'
import { Icon } from 'blaze-ui/icons/Types'
import { uniqBy } from 'lodash'
import { IntegrationInsights } from './IntegrationInsights'
import { Heading } from 'blaze-ui/Heading/Heading'
import { LoadingMessage } from './LoadingMessage'

export interface PerformanceInsightsWithIntegrationsProps {
  isConnectingFirstIntegration: boolean
  onNext: () => void
  onBack: () => void
  brandKitId: number
  insightChannels: string[]
}

export const PerformanceInsightsWithIntegrations = ({
  isConnectingFirstIntegration,
  onNext,
  onBack,
  brandKitId,
  insightChannels,
}: PerformanceInsightsWithIntegrationsProps) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const { connect } = useConnectAccountPopup({ showToast: false })

  const onConnect = (popUp?: Window) => {
    connect(popUp, {
      onConnect: () => {
        queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', 'detail', brandKitId])
      },
    })
  }

  const onConnectionPress = () => {
    connect(undefined, {
      onConnect: () => {
        queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', 'detail', brandKitId])
      },
    })
  }
  const { data } = useBrandKit(brandKitId)
  const brandKit = data?.brand_kit

  const integrations = brandKit?.external_integrations.filter((integration) =>
    [
      ExternalIntegrationEnum.Instagram,
      ExternalIntegrationEnum.Facebook,
      ExternalIntegrationEnum.LinkedIn,
      ExternalIntegrationEnum.TikTok,
      ExternalIntegrationEnum.X,
    ].includes(integration.provider),
  )

  const otherChannels = uniqBy(
    [
      ...organicChannels.filter(
        (channel) =>
          insightChannels.includes(channel.name) &&
          !integrations?.some((integration) => integration.provider === channel.name && !!integration),
      ),
      ...organicChannels.filter(
        (channel) => !integrations?.some((integration) => integration.provider === channel.name && !!integration),
      ),
    ],
    'name',
  ).slice(0, 3 - (integrations?.length ?? 0))

  if (isConnectingFirstIntegration && !integrations?.length) {
    return (
      <MultiStepStepLayout title="Your performance insights" description="Based on your last 30 days of posts">
        <div className={Styles.pageWrapper}>
          <StickyFooterProvider
            footer={
              <StickyFooter>
                <FooterButtons>
                  <Button variant="tertiary" size="lg" onPress={onBack} aria-label="Back">
                    Back
                  </Button>
                  <Button size="lg" variant="primary" onPress={onNext} aria-label="Next">
                    Continue to recommendations
                  </Button>
                </FooterButtons>
              </StickyFooter>
            }
          >
            <div className={Styles.insightsContainer}>
              <LoadingMessage label="Connecting account" />
            </div>
          </StickyFooterProvider>
        </div>
      </MultiStepStepLayout>
    )
  }

  return (
    <MultiStepStepLayout title="Your performance insights" description="Based on your last 30 days of posts">
      <div className={Styles.pageWrapper}>
        <StickyFooterProvider
          footer={
            <StickyFooter>
              <FooterButtons>
                <Button variant="tertiary" size="lg" onPress={onBack} aria-label="Back">
                  Back
                </Button>
                <Button size="lg" variant="primary" onPress={onNext} aria-label="Next">
                  Continue to recommendations
                </Button>
              </FooterButtons>
            </StickyFooter>
          }
        >
          <div className={Styles.insightsContainer}>
            {integrations?.map((integration) => (
              <IntegrationInsights integration={integration} brandKitId={brandKitId} key={integration.id} />
            ))}
          </div>

          <div className={Styles.connectMoreContainer}>
            {otherChannels.length > 0 && (
              <div className={Styles.connectMoreContainer}>
                <Heading level={5}>Don't wait! Connect more platforms for complete insights</Heading>
                <div className={Styles.connectMoreButtons}>
                  {otherChannels.map((channel) => (
                    <InitiateOauthTrigger
                      provider={channel.name as ExternalIntegrationEnum}
                      popUp
                      onInitiatedOauth={onConnect}
                      key={channel.name}
                    >
                      <Button
                        size="lg"
                        variant="secondary"
                        frontIcon={channel.icon as Icon}
                        onPress={onConnectionPress}
                      >
                        Connect {channel.title}
                      </Button>
                    </InitiateOauthTrigger>
                  ))}
                </div>
              </div>
            )}
          </div>
        </StickyFooterProvider>
      </div>
    </MultiStepStepLayout>
  )
}
